// ./src/hooks/useGuestDraws.ts
//
// Loads an event's prize-draw rounds (active + disabled), by display order, with their winners, ONE PAGE at a time with Load more (ROADMAP
// #112 — it read every one at once). `total` is the server's count for the whole set; `reload` re-reads page 1 after
// a change. The first-read rule: `isLoaded` is the first read only (released on failure too).

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { DEFAULT_PAGE_SIZE } from "@/config/constants";
import { GuestDrawService, GuestDraw } from "@/utils/api";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseGuestDrawsReturn {
  draws: GuestDraw[];
  /** How many there are in all (the server's count), null until read. */
  total: number | null;
  loading: boolean;
  loadingMore: boolean;
  /** True once the first fetch has completed (list may still be empty). */
  isLoaded: boolean;
  hasMore: boolean;
  error: OpResult | null;
  loadMore: () => Promise<void>;
  reload: () => Promise<void>;
}

const STATUSES_SHOWN = [STATUSES.ACTIVE, STATUSES.DISABLED];

export const useGuestDraws = (eventId?: string | null): UseGuestDrawsReturn => {
  const [draws, setItems] = useState<GuestDraw[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const callIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!eventId) {
      setItems([]);
      setTotal(0);
      setIsLoaded(true);

      return;
    }

    const myCall = ++callIdRef.current;
    setLoading(true);
    // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
    setLoadingMore(false);
    setError(null);

    const result = await GuestDrawService.list(eventId, STATUSES_SHOWN, { page: 1, pageSize: DEFAULT_PAGE_SIZE });

    if (myCall !== callIdRef.current) {
      return;
    }

    setLoading(false);
    setIsLoaded(true);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      setItems(result.data as GuestDraw[]);
      setTotal(typeof result.total === "number" ? result.total : null);
      setPage(1);
    } else {
      setError(result);
      setItems([]);
      setTotal(null);
    }
  }, [eventId]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, isLoaded);

  useEffect(() => {
    void load();
  }, [load]);

  const hasMore = total !== null && draws.length < total;

  const loadMore = useCallback(async () => {
    if (!eventId || loading || loadingMore || !hasMore) {
      return;
    }

    const myCall = callIdRef.current;
    const nextPage = page + 1;
    setLoadingMore(true);
    const result = await GuestDrawService.list(eventId, STATUSES_SHOWN, { page: nextPage, pageSize: DEFAULT_PAGE_SIZE });

    if (myCall !== callIdRef.current) {
      return;
    }

    setLoadingMore(false);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      const rows = result.data as GuestDraw[];
      setItems((prev) => [...prev, ...rows.filter((r) => !prev.some((p) => p.id === r.id))]);
      setPage(nextPage);
    } else {
      // A failed Load more keeps what is on screen.
      setError(result);
    }
  }, [eventId, loading, loadingMore, hasMore, page]);

  return { draws, total, loading, loadingMore, isLoaded, hasMore, error, loadMore, reload: load };
};
